import React, { useState } from "react";
import { saveSupplierContact } from "@/lib/apiClient";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Mail, Check, Pencil, Loader2, BellOff, Bell } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export const SupplierContactEditor = ({ supplierKey, contact, onSaved }) => {
  const { user } = useApp();
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState(contact?.email || "");
  const [busy, setBusy] = useState(false);

  const save = async (notify = contact?.notify ?? true) => {
    setBusy(true);
    try {
      const saved = await saveSupplierContact(supplierKey, { email: email || null, notify, user });
      toast.success(saved.email ? `Supplier email saved — status emails go to ${saved.email}` : "Supplier email cleared");
      onSaved(saved);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const toggleNotify = async () => {
    setBusy(true);
    try {
      const saved = await saveSupplierContact(supplierKey, { email: contact?.email || null, notify: !(contact?.notify ?? true), user });
      onSaved(saved);
      toast.success(saved.notify ? "Status emails on" : "Status emails muted for this supplier");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 border-t border-border pt-3 flex flex-wrap items-center gap-2 text-xs" data-testid="supplier-contact">
      <Mail className="h-3.5 w-3.5 text-muted-foreground" />
      {editing ? (
        <>
          <Input data-testid="contact-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="accounts@supplier.com" className="h-7 w-56 text-xs" onKeyDown={(e) => e.key === "Enter" && save()} />
          <Button data-testid="contact-save-btn" size="sm" className="h-7 px-2" onClick={() => save()} disabled={busy}>{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}</Button>
          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => { setEditing(false); setEmail(contact?.email || ""); }}>Cancel</Button>
        </>
      ) : (
        <>
          <span className={contact?.email ? "font-mono" : "text-muted-foreground italic"} data-testid="contact-email">{contact?.email || "No supplier email — status emails not sent"}</span>
          <button data-testid="contact-edit-btn" onClick={() => setEditing(true)} className="text-muted-foreground hover:text-foreground"><Pencil className="h-3 w-3" /></button>
          {contact?.email && (
            <button data-testid="contact-notify-toggle" onClick={toggleNotify} disabled={busy} className={`ml-auto inline-flex items-center gap-1 rounded-md border px-2 py-0.5 ${contact.notify ? "border-emerald-500/40 text-emerald-500" : "border-border text-muted-foreground"}`}>
              {contact.notify ? <Bell className="h-3 w-3" /> : <BellOff className="h-3 w-3" />} {contact.notify ? "Emails on" : "Muted"}
            </button>
          )}
        </>
      )}
    </div>
  );
};
